// Shared EW data-model helpers — bridge the BE wire shape (flat `prices[]` +
// `program_groups[]`) to the 2-level category × duration view the PDP selector
// renders. Kept component-agnostic so cart / mini-cart / checkout can reuse it.

/**
 * Group a flat EW `prices[]` into categories by `code`, labelled via
 * `program_groups[]` ([{ code, label }], label already localised by BE).
 * Durations within a category are sorted by year ascending.
 */
export function groupPricesByCategory(prices = [], programGroups = []) {
  const labelByCode = {};
  (programGroups || []).forEach((g) => {
    if (g && g.code != null) labelByCode[g.code] = g.label;
  });

  const order = [];
  const byCode = {};
  (prices || []).forEach((p) => {
    if (!p || p.code == null) return;
    if (!byCode[p.code]) {
      byCode[p.code] = [];
      order.push(p.code);
    }
    byCode[p.code].push(p);
  });

  return order.map((code) => ({
    category_id: code,
    label: labelByCode[code] != null ? labelByCode[code] : code,
    durations: byCode[code]
      .slice()
      .sort((a, b) => (a.year || 0) - (b.year || 0))
      .map((p) => ({
        price_id: p.price_id,
        year: p.year,
        duration: p.duration,
        price: p.price,
        logo: p.logo,
        code: p.code,
        is_selected: p.is_selected,
      })),
  }));
}

/**
 * Round a price for display only — the raw decimal is kept for checkout,
 * just the visible text is rounded to the nearest whole number.
 */
export function roundPriceForDisplay(price) {
  const num = Number(price);
  if (Number.isNaN(num)) return String(price);
  return String(Math.round(num));
}

export function getEWSummaryForLine(item) {
  return item?.extension_attributes?.extended_warranty_summary || [];
}

/**
 * Normalise the EW endpoint's `configuration` block into header content.
 * Title/label are BE-localised → returned null when empty so the caller can
 * apply its i18n fallback. Logo URLs arrive pre-parametrised (?width=…), so the
 * query is stripped before ImgTag appends its own (avoids a double query).
 */
export function getEwHeaderConfig(configuration) {
  const cfg = configuration || {};
  const stripQuery = (url) => (url ? url.split("?")[0] : url);
  return {
    title: (cfg.extended_warranty_title || "").trim() || null,
    moreInfoLabel: cfg.more_info_title || null,
    almaneaLogo: stripQuery(cfg.extended_warranty_logo) || "/logo.webp",
    tawniaLogo: stripQuery(cfg.tawnia_logo) || null,
  };
}
